import { FC, ReactElement } from 'react';
import { history } from 'umi';
import { Grid } from 'antd';
import { useSelector } from 'dva';
import { LoginPage, Form, Typography } from 'iglooform';
import { useIntl } from 'react-intl';
import { SwitchLang } from '@/components/switch-lang';
import { setPassword } from '@/service/global';
import { reg, chineseReg } from '@/utils/reg';
import _ from 'lodash';
import CryptoJS from 'crypto-js';

import styles from '../index.less';

const ResetPasswordPage: FC = (): ReactElement => {
  const { formatMessage } = useIntl();
  const { md } = Grid.useBreakpoint();
  const form = Form.useForm();
  const symbols = `~\`!@#$%^&*()_-+={[}]|\\:;"'<,>.?/`;

  const { token } = useSelector<ReduxState, GlobalState>(
    (state) => state.global,
  );

  const handleSubmit = _.throttle(
    () => {
      const password = form.getFieldValue('resetPassword');
      if (!!token) {
        setPassword(Encrypt(password), token as string)
          .then((res) => {
            if (res.status === 'ok') {
              history.replace('/password/result/success', { type: 'reset' });
            }
          })
          .catch((err) => console.error(err));
      }
    },
    500,
    { leading: true, trailing: false },
  );

  function Encrypt(password: string) {
    const encrypted = CryptoJS.DES.encrypt(
      CryptoJS.enc.Utf8.parse(password),
      CryptoJS.enc.Utf8.parse(ENCRYPT_KEY),
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.ZeroPadding, //CryptoJS.pad.Pkcs7
      },
    );
    return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
  }

  return (
    <LoginPage showAppStore>
      <SwitchLang
        className={md ? styles.selectLang : styles.selectLangMobile}
      />
      <div className={md ? styles.loginContent : styles.loginContentMobile}>
        <div className={styles.title}>
          <Typography level="h1">
            {formatMessage({
              id: 'Reset Your Password',
              defaultMessage: 'Reset Your Password',
            })}
          </Typography>
        </div>
        <Form
          form={form}
          config={{
            type: 'Login',
            elements: [
              {
                type: 'Password',
                label: formatMessage({
                  id: 'New Password',
                  defaultMessage: 'New Password',
                }),
                name: 'resetPassword',
                fullRow: true,
                required: true,
                initialValue: '',
                extra: formatMessage(
                  {
                    id: `Password Validate`,
                  },
                  { symbols },
                ),
                rules: [
                  {
                    validator: (_, value: string) => {
                      if (
                        !!value &&
                        (!reg.test(value) || chineseReg.test(value))
                      ) {
                        return Promise.reject(
                          new Error(
                            formatMessage({
                              id: 'The password is not validated',
                            }),
                          ),
                        );
                      } else {
                        return Promise.resolve();
                      }
                    },
                  },
                ],
              },
              {
                type: 'Password',
                label: formatMessage({
                  id: 'Confirm New Password',
                  defaultMessage: 'Confirm New Password',
                }),
                name: 'confirmNewPassword',
                fullRow: true,
                required: true,
                initialValue: '',
                rules: [
                  ({ getFieldValue }) => ({
                    validator: (_, value: string) => {
                      const p = getFieldValue('resetPassword');
                      if (!!value && !!p && p !== value) {
                        return Promise.reject(
                          new Error(
                            formatMessage({
                              id: 'The two passwords are inconsistent',
                            }),
                          ),
                        );
                      } else {
                        return Promise.resolve();
                      }
                    },
                  }),
                ],
                antdDependencies: ['resetPassword'],
              },
            ],
          }}
          onSubmit={handleSubmit}
          locales={{
            submitText: formatMessage({
              id: 'Reset Password',
              defaultMessage: 'Reset Password',
            }),
            validateMessages: {
              required: formatMessage({ id: 'Required' }),
            },
          }}
        />
      </div>
    </LoginPage>
  );
};

export default ResetPasswordPage;
